import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ShoppingBag, TrendingUp, Users, Ban, Loader2, CheckCircle2,
  XCircle, RotateCcw, ShieldOff, PlayCircle,
} from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { apiFetch } from "@/lib/api";

/**
 * Phase 24e · Admin marketplace dashboard.
 * Route: /admin/marketplace-v24e
 * Overview + transactions + payouts + creator bans.
 */
export default function AdminMarketplaceV24e() {
  const [overview, setOverview] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [banned, setBanned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [cronBusy, setCronBusy] = useState(false);

  async function loadAll() {
    setLoading(true);
    try {
      const [ov, po, tx, bl] = await Promise.all([
        apiFetch("/admin/marketplace/overview"),
        apiFetch("/admin/marketplace/payouts?limit=100"),
        apiFetch("/admin/marketplace/transactions?limit=100"),
        apiFetch("/admin/marketplace/creators/banned"),
      ]);
      setOverview(ov);
      setPayouts(po?.items || []);
      setTransactions(tx?.items || []);
      setBanned(bl?.items || []);
    } catch (e) {
      toast.error(`Errore caricamento: ${String(e?.message || e)}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  async function approvePayout(id) {
    try {
      await apiFetch(`/admin/marketplace/payouts/${id}/approve`, {
        method: "POST", body: JSON.stringify({ reason: "admin approved" }),
      });
      toast.success("Payout approvato");
      await loadAll();
    } catch (e) { toast.error(String(e?.message || e)); }
  }

  async function rejectPayout(id) {
    const reason = window.prompt("Motivo del rifiuto?");
    if (!reason) return;
    try {
      await apiFetch(`/admin/marketplace/payouts/${id}/reject`, {
        method: "POST", body: JSON.stringify({ reason }),
      });
      toast.success("Payout rifiutato");
      await loadAll();
    } catch (e) { toast.error(String(e?.message || e)); }
  }

  async function runWeeklyCron() {
    setCronBusy(true);
    try {
      const r = await apiFetch("/admin/marketplace/cron/run-weekly", { method: "POST" });
      toast.success(`Cron: ${r?.approved || 0} approvati / ${r?.processed || 0} processati`);
      await loadAll();
    } catch (e) { toast.error(String(e?.message || e)); }
    finally { setCronBusy(false); }
  }

  async function unbanCreator(cid) {
    if (!window.confirm(`Sbloccare ${cid.slice(0, 8)}…?`)) return;
    try {
      await apiFetch(`/admin/marketplace/creators/${cid}/unban`, { method: "POST" });
      toast.success("Creator sbloccato");
      await loadAll();
    } catch (e) { toast.error(String(e?.message || e)); }
  }

  async function forceRefund(txId) {
    const reason = window.prompt("Motivo del force-refund?");
    if (!reason) return;
    try {
      await apiFetch(`/admin/marketplace/purchases/${txId}/force-refund`, {
        method: "POST", body: JSON.stringify({ reason }),
      });
      toast.success("Refund forzato");
      await loadAll();
    } catch (e) { toast.error(String(e?.message || e)); }
  }

  const eur = (c) => (Number(c || 0) / 100).toFixed(2).replace(".", ",");

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text-main)]">
      <Header />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 p-6 md:p-10 max-w-7xl mx-auto w-full">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <div className="mb-8 flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-text-secondary mb-2">
                  Phase 24e · Admin Console
                </div>
                <h1 className="text-3xl md:text-4xl font-semibold tracking-tight" data-testid="admin-mp-title">
                  Marketplace V4
                </h1>
                <p className="text-sm text-text-secondary mt-2">
                  Revenue split 70/30 · Payout cron Mon 06:00 UTC · 3-tier creator bans
                </p>
              </div>
              <button
                data-testid="admin-run-cron-btn"
                onClick={runWeeklyCron}
                disabled={cronBusy}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--color-primary)]/60 bg-[var(--color-primary)]/10 text-[var(--color-primary)] text-xs uppercase tracking-widest font-semibold hover:bg-[var(--color-primary)]/20 transition-colors"
              >
                {cronBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
                Run Weekly Payout Cron
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* KPI */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <KpiCard icon={TrendingUp} label="Sales gross" value={`$${eur(overview?.sales_gross_cents)}`} tone="success" hint={`${overview?.sales_count || 0} vendite`} testid="kpi-sales" />
                  <KpiCard icon={ShoppingBag} label="Platform net" value={`$${eur(overview?.platform_net_cents)}`} tone="primary" hint="Net revenue" testid="kpi-platform" />
                  <KpiCard icon={RotateCcw} label="Refunds" value={`$${eur(overview?.refunds_gross_cents)}`} tone="alert" hint={`${overview?.refunds_count || 0} rimborsi`} testid="kpi-refunds" />
                  <KpiCard icon={Ban} label="Banned creators" value={String(overview?.banned_creators || 0)} tone="secondary" hint={`${overview?.pending_payouts || 0} payout pending`} testid="kpi-bans" />
                </div>

                {/* Tabs */}
                <div className="flex gap-2 border-b border-[var(--color-border)]">
                  {["overview", "payouts", "transactions", "bans"].map((t) => (
                    <button
                      key={t}
                      data-testid={`tab-${t}`}
                      onClick={() => setTab(t)}
                      className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold border-b-2 transition-colors ${
                        tab === t
                          ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                          : "border-transparent text-text-secondary hover:text-[var(--color-text-main)]"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>

                {tab === "overview" && (
                  <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 text-sm text-text-secondary">
                    <p>Sales count: <span className="text-[var(--color-text-main)] tabular-nums">{overview?.sales_count || 0}</span></p>
                    <p>Sales gross: <span className="text-[var(--color-text-main)] tabular-nums">${eur(overview?.sales_gross_cents)}</span></p>
                    <p>Refunds: <span className="text-[var(--color-text-main)] tabular-nums">${eur(overview?.refunds_gross_cents)}</span></p>
                    <p>Platform net: <span className="text-[var(--color-text-main)] tabular-nums">${eur(overview?.platform_net_cents)}</span></p>
                    <p>Creator net: <span className="text-[var(--color-text-main)] tabular-nums">${eur(overview?.creator_net_cents)}</span></p>
                  </div>
                )}

                {tab === "payouts" && (
                  <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
                    {payouts.length === 0 ? (
                      <p className="text-xs text-text-secondary">Nessun payout registrato.</p>
                    ) : (
                      <div className="space-y-2">
                        {payouts.map((p) => (
                          <div
                            key={p.id}
                            data-testid={`admin-payout-${p.id}`}
                            className="flex items-center justify-between p-3 rounded-lg bg-[var(--color-bg)] border border-white/5"
                          >
                            <div>
                              <div className="text-sm font-semibold tabular-nums">${eur(p.amount_cents)}</div>
                              <div className="text-[10px] text-text-secondary font-mono">
                                {p.creator_id?.slice(0, 12)}… · {p.status}
                              </div>
                            </div>
                            {p.status === "pending" ? (
                              <div className="flex gap-2">
                                <button
                                  data-testid={`approve-payout-${p.id}`}
                                  onClick={() => approvePayout(p.id)}
                                  className="text-[10px] px-3 py-1 rounded-full bg-[var(--color-success)]/20 text-[var(--color-success)] border border-[var(--color-success)]/40 inline-flex items-center gap-1"
                                >
                                  <CheckCircle2 className="h-3 w-3" /> Approve
                                </button>
                                <button
                                  data-testid={`reject-payout-${p.id}`}
                                  onClick={() => rejectPayout(p.id)}
                                  className="text-[10px] px-3 py-1 rounded-full bg-[var(--color-alert)]/20 text-[var(--color-alert)] border border-[var(--color-alert)]/40 inline-flex items-center gap-1"
                                >
                                  <XCircle className="h-3 w-3" /> Reject
                                </button>
                              </div>
                            ) : (
                              <span className="text-[10px] uppercase tracking-widest text-text-secondary">
                                {p.status}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {tab === "transactions" && (
                  <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
                    <div className="space-y-2">
                      {transactions.slice(0, 50).map((t) => (
                        <div
                          key={t.id}
                          data-testid={`admin-tx-${t.id}`}
                          className="flex items-center justify-between p-3 rounded-lg bg-[var(--color-bg)] border border-white/5"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold truncate">
                              {t.asset_title || t.kind} · {t.kind}
                            </div>
                            <div className="text-[10px] text-text-secondary font-mono">
                              {t.id?.slice(0, 12)}… · buyer={t.buyer_user_id?.slice(0, 8) || "-"} · creator={t.creator_id?.slice(0, 8) || "-"}
                            </div>
                          </div>
                          <div className="text-right flex items-center gap-3">
                            <div>
                              <div className={`text-sm font-semibold tabular-nums ${t.kind === "refund" ? "text-[var(--color-alert)]" : "text-[var(--color-text-main)]"}`}>
                                ${eur(t.gross_cents)}
                              </div>
                              <div className="text-[10px] uppercase tracking-widest text-text-secondary">{t.status}</div>
                            </div>
                            {t.kind === "sale" && t.status === "completed" && (
                              <button
                                data-testid={`force-refund-${t.id}`}
                                onClick={() => forceRefund(t.id)}
                                className="text-[10px] px-2 py-1 rounded-full border border-[var(--color-alert)]/40 text-[var(--color-alert)] hover:bg-[var(--color-alert)]/10 transition-colors"
                              >
                                Force Refund
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {tab === "bans" && (
                  <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
                    {banned.length === 0 ? (
                      <p className="text-xs text-text-secondary">Nessun creator bannato.</p>
                    ) : (
                      <div className="space-y-2">
                        {banned.map((b) => (
                          <div
                            key={b.creator_id}
                            data-testid={`banned-creator-${b.creator_id}`}
                            className="flex items-center justify-between p-3 rounded-lg bg-[var(--color-bg)] border border-white/5"
                          >
                            <div>
                              <div className="flex items-center gap-2 text-sm font-semibold">
                                <Ban className="h-4 w-4 text-[var(--color-alert)]" />
                                {b.creator_id?.slice(0, 16)}…
                                <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest ${
                                  b.tier === "permaban" ? "bg-[var(--color-alert)]/20 text-[var(--color-alert)]" :
                                  b.tier === "hard" ? "bg-neutral-800 text-neutral-200" :
                                  "bg-white/10 text-text-secondary"
                                }`}>
                                  {b.tier}
                                </span>
                              </div>
                              <div className="text-[10px] text-text-secondary mt-1">
                                {b.reason} · {new Date(b.banned_at).toLocaleString("it-IT")}
                              </div>
                            </div>
                            <button
                              data-testid={`unban-${b.creator_id}`}
                              onClick={() => unbanCreator(b.creator_id)}
                              className="text-[10px] px-3 py-1 rounded-full border border-[var(--color-success)]/40 text-[var(--color-success)] hover:bg-[var(--color-success)]/10 inline-flex items-center gap-1"
                            >
                              <ShieldOff className="h-3 w-3" /> Unban
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </main>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tone = "primary", hint, testid }) {
  const toneCls = {
    primary: "text-[var(--color-primary)]",
    secondary: "text-[var(--color-secondary)]",
    success: "text-[var(--color-success)]",
    alert: "text-[var(--color-alert)]",
  }[tone];
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-2">
        <Icon className={`h-3 w-3 ${toneCls}`} /> {label}
      </div>
      <div className={`text-2xl font-semibold tabular-nums ${toneCls}`}>{value}</div>
      {hint && <p className="text-[10px] text-text-secondary mt-1 leading-tight">{hint}</p>}
    </div>
  );
}
